import React from 'react'
import DOMPurify from 'dompurify'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import {
  IconCaretLeft,
  IconExternalLink,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { shouldUseCvss3 } from '../../sca/libraries/helpers/cvssUtils'
import { CvssScoringType } from '../../sca/libraries/helpers/ui-interfaces'
import { Vulnerability } from '../shared'
import { Severity, VulnerabilitySeverityBadge } from '../vulnerability-severity'
import { VulnerabilitySeverityName } from '../vulnerability-severity/severity'
import { VulnerabilityModalVectors } from './vulnerability-dialog-vectors'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { Button } from '@contrast-security-inc/design-system-buttons'
import {
  Modal,
  ModalBody,
  ModalFooter
} from '@contrast-security-inc/design-system-popovers'

export const modalContainerDataTestID = 'vuln-modal-container'

interface Props {
  vulnerability: Vulnerability
  isOpen: boolean
  isCVEModalOpen: boolean
  isUsedInDependencyTree?: boolean
  onCancel: () => void
  onBack?: () => void
  cvssScoringType?: CvssScoringType
}

export const VulnerabilityModal = ({
  vulnerability,
  isOpen,
  isCVEModalOpen,
  isUsedInDependencyTree,
  onBack,
  onCancel,
  cvssScoringType = CvssScoringType.CVSS2
}: Props) => {
  const { formatMessage } = useIntl()

  if (vulnerability) {
    const {
      name,
      description,
      visible,
      references,
      severityCode,
      hasCve,
      cvss3SeverityValue,
      cvss3SeverityCode
    } = vulnerability

    const severityBadge = Severity.fromName(
      shouldUseCvss3(vulnerability, cvssScoringType)
        ? (cvss3SeverityCode as VulnerabilitySeverityName)
        : (severityCode as VulnerabilitySeverityName)
    )

    const cvssVersion = shouldUseCvss3(vulnerability, cvssScoringType)
      ? `CVSS 3.1`
      : `CVSS 2.0`

    const severityValue = shouldUseCvss3(vulnerability, cvssScoringType)
      ? cvss3SeverityValue
      : vulnerability.severityValue
    return (
      <Modal
        size="md"
        variant="info"
        testId={modalContainerDataTestID}
        isOpen={isOpen}
        onClose={onCancel}
        zIndex={theme.zIndex.popover + 1}
        title={
          <>
            {!isCVEModalOpen && isUsedInDependencyTree ? (
              <div
                onClick={onBack}
                className={css`
                  cursor: pointer;
                `}
              >
                <IconCaretLeft size="md" color="black" />
                {formatMessage({ id: 'action_back' })}
              </div>
            ) : (
              name
            )}
          </>
        }
      >
        <ModalBody>
          {visible ? (
            <>
              {!isCVEModalOpen && isUsedInDependencyTree ? (
                <div
                  className={css`
                    display: flex;
                    align-items: center;
                    font-family: 'primary';
                    font-size: '17px';
                    font-weight: 700;
                    color: 'black';
                  `}
                >
                  <span
                    className={css`
                      padding-right: 5px;
                      margin-bottom: 5px;
                      font-weight: bold;
                    `}
                  >
                    {name}
                  </span>
                </div>
              ) : null}
              <div
                className={css`
                  display: flex;
                  align-items: center;
                  margin-bottom: 10px;
                `}
              >
                <VulnerabilitySeverityBadge severity={severityBadge} />
                {hasCve && severityValue ? (
                  <div
                    className={css`
                      display: grid;
                      grid-template-rows: 1fr;
                      grid-template-columns: auto auto auto 1fr;
                      align-items: center;
                      padding-bottom: 2px;
                      padding-left: 7px;
                      font-size: 16px;
                      width: 100%;
                    `}
                  >
                    <span
                      className={css`
                        padding-right: 4px;
                      `}
                    >
                      <span data-e2e={'CVSS-Version'}>{cvssVersion}</span>
                    </span>
                    <Text
                      className={css`
                        padding-left: 5px;
                      `}
                      font="monoStrong"
                    >
                      {formatMessage({ id: 'title_score' })}
                    </Text>
                    <span>&nbsp; {severityValue.toFixed(1)}</span>
                    <Link
                      isExternal
                      font="body"
                      className={css`
                        display: flex;
                        align-items: center;
                        justify-self: flex-end;
                      `}
                      href={`https://nvd.nist.gov/vuln/detail/${name}`}
                    >
                      {formatMessage({ id: 'title_csvLink' })}
                    </Link>
                  </div>
                ) : null}
                {!hasCve || !severityValue ? (
                  <div
                    className={css`
                      display: grid;
                      grid-template-rows: 1fr;
                      grid-template-columns: auto auto auto 1fr;
                      align-items: center;
                      padding-bottom: 2px;
                      padding-left: 7px;
                      font-size: 16px;
                      width: 100%;
                    `}
                  >
                    <Link
                      isExternal
                      font="body"
                      className={css`
                        display: flex;
                        align-items: center;
                        justify-self: flex-end;
                      `}
                      href={`https://nvd.nist.gov/vuln/detail/${name}`}
                    >
                      {formatMessage({ id: 'title_csvLink' })}
                      <IconExternalLink
                        className={css`
                          padding-left: 5px;
                        `}
                        size="md"
                      />
                    </Link>
                  </div>
                ) : null}
              </div>
              <div>
                <span
                  data-testid="sanitized-text-container"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description)
                  }}
                />
              </div>
              {hasCve ? (
                <VulnerabilityModalVectors
                  vulnerability={vulnerability}
                  cvssScoringType={cvssScoringType}
                />
              ) : null}
              {references && references.length > 0 ? (
                <div
                  className={css`
                    margin-top: 15px;
                  `}
                >
                  <Text
                    className={css`
                      margin-bottom: 15px;
                    `}
                    color="neutral80"
                  >
                    {formatMessage({ id: 'title_references' })}
                  </Text>
                  <ul>
                    {references &&
                      references.map((reference) => (
                        <li
                          key={reference.href}
                          className={css`
                            display: flex;
                            align-items: center;
                          `}
                        >
                          <Link isExternal href={reference.href}>
                            {reference.href}
                          </Link>
                          <IconExternalLink
                            className={css`
                              padding-left: 5px;
                            `}
                            size="sm"
                            color="black"
                          />
                        </li>
                      ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <div>
              {formatMessage({ id: 'detail_libraryDashboard_noInfoAboutVuln' })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={onCancel} autoFocus>
            {formatMessage({ id: 'action_done' })}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  return null
}
